import { LayoutDefinitionBuilder } from "@/shared/domain/layouts";
import {
  MessagePathNode,
  TopicNode,
  pathMatcher,
  pathParser,
} from "@/shared/domain/topics";
import type { TopicData } from "@/shared/state/visualization";
import type { Axes } from "@/shared/state/visualization/schema/v1/three-d";

export type AxisName = "forward" | "right" | "up";

export interface PreservedAxis {
  axis: AxisName;
  node: MessagePathNode;
}

/**
 * Given a component's current Axes (Position, VectorField, ...) and the topic
 * tree for the file the user has just switched to, returns each axis whose
 * existing path still resolves in the new file, paired with the matched
 * MessagePathNode for the caller to convert to TopicData. Axes that do not
 * resolve are simply absent from the result; the caller resets them to 0.
 *
 * Matching mirrors `LayoutRuleMatcher.findMatches` — parse the axis's path,
 * walk the new tree via `pathMatcher`, keep complete matches — except the
 * data-type check is deliberately more permissive: a missing
 * `canonical_data_type` on the original axis lets any type through, where
 * `LayoutRuleMatcher` enforces equality strictly.
 *
 * Pure function. No dispatches, no `.toTopicData()` calls (which can throw on
 * topics without a default representation). The caller owns those concerns.
 */
export function reconcileAxisPathsToFile(
  axes: Axes | undefined,
  newFileTopicTree: TopicNode[],
): PreservedAxis[] {
  if (!axes) {
    return [];
  }

  const entries: Array<[AxisName, TopicData | number]> = [
    ["forward", axes.forward],
    ["right", axes.right],
    ["up", axes.up],
  ];

  const preserved: PreservedAxis[] = [];
  for (const [axis, value] of entries) {
    if (typeof value === "number") {
      continue;
    }
    const node = matchAxisInTree(value, newFileTopicTree);
    if (node) {
      preserved.push({ axis, node });
    }
  }
  return preserved;
}

function matchAxisInTree(
  axis: TopicData,
  topicTree: TopicNode[],
): MessagePathNode | undefined {
  const requirement =
    LayoutDefinitionBuilder.createRequirementFromTopicData(axis);
  const parsed = pathParser.parse(requirement.topicMessagePath);
  const { dataType } = requirement;
  return pathMatcher
    .findMatches(parsed, topicTree)
    .filter(
      (match) =>
        match.isComplete &&
        (dataType === undefined ||
          match.match.data.canonical_data_type === dataType),
    )
    .map((match) => match.match)[0];
}
